import { useQuery } from "@tanstack/react-query";
import type { PurchaseReviewDto } from "@gaole/shared";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { StarRating } from "./StarRating";
import { PageLoader } from "../ui";

type ReviewsResponse = {
  summary: { avgRating: number; ratingCount: number };
  items: PurchaseReviewDto[];
};

export function ProductReviewsPanel({ productId }: { productId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["reviews", "product", productId],
    queryFn: () => api<ReviewsResponse>(`/reviews/products/${productId}?pageSize=20`),
  });

  if (isLoading) return <PageLoader />;
  if (!data?.summary.ratingCount) {
    return <p className="text-xs text-white/45">No reviews yet — verified buyers can leave one after delivery.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <StarRating value={Math.round(data.summary.avgRating)} readOnly size={18} />
        <span className="text-sm font-semibold text-white">{data.summary.avgRating.toFixed(1)}</span>
        <span className="text-xs text-white/45">({data.summary.ratingCount} reviews)</span>
      </div>
      <ul className="space-y-3">
        {data.items.map((r) => (
          <li key={r.id} className="rounded-2xl border border-white/5 bg-surface p-3.5">
            <div className="flex items-center justify-between gap-2">
              <StarRating value={r.rating} readOnly size={14} />
              <span className="text-[10px] text-white/40">{fmtDate(r.createdAt)}</span>
            </div>
            <p className="mt-1 text-[11px] font-medium text-white/70">{r.buyerName}</p>
            {r.comment ? <p className="mt-2 text-xs leading-relaxed text-white/80">{r.comment}</p> : null}
            {r.images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {r.images.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded-lg border border-white/10">
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ListingReviewsPanel({ listingId }: { listingId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["reviews", "listing", listingId],
    queryFn: () => api<ReviewsResponse>(`/reviews/listings/${listingId}?pageSize=20`),
  });

  if (isLoading) return <PageLoader />;
  if (!data?.summary.ratingCount) {
    return <p className="text-xs text-white/45">No reviews yet.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <StarRating value={Math.round(data.summary.avgRating)} readOnly size={18} />
        <span className="text-sm font-semibold text-white">{data.summary.avgRating.toFixed(1)}</span>
        <span className="text-xs text-white/45">({data.summary.ratingCount})</span>
      </div>
      <ul className="space-y-3">
        {data.items.map((r) => (
          <li key={r.id} className="rounded-2xl border border-white/5 bg-surface p-3.5">
            <div className="flex items-center justify-between gap-2">
              <StarRating value={r.rating} readOnly size={14} />
              <span className="text-[10px] text-white/40">{fmtDate(r.createdAt)}</span>
            </div>
            <p className="mt-1 text-[11px] font-medium text-white/70">{r.buyerName}</p>
            {r.comment ? <p className="mt-2 text-xs text-white/80">{r.comment}</p> : null}
            {r.images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {r.images.map((url) => (
                  <img key={url} src={url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
